import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { analyzeATS } from '../services/openaiService';
import { useUser } from '../contexts/UserContext';
import { useFeatureGate } from '../hooks/useFeatureGate';
import './ATSCheckStudio.css';
import './ATSCheckStudio.theme.css';

// Same worker source the other web-side pdf consumers use (the Electron panes
// import the bundled worker entry instead — don't unify these, the desktop
// build has no network guarantee).
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const CHECKLIST_ITEMS = [
  ['contactInfo', 'Contact Information', 'Add your phone number, email, and location near the top.'],
  ['education', 'Education Section', 'List your degrees with institution names and dates.'],
  ['experience', 'Work Experience', 'Detail each role with quantified accomplishments, not duties.'],
  ['skills', 'Skills Section', 'Add a dedicated skills block mirroring the job description.'],
  ['projects', 'Projects', 'Include relevant projects to show practical, hands-on work.'],
  ['certifications', 'Certifications', 'List any certifications or licenses relevant to this role.'],
];

/* Tone by score — clear / borderline / likely rejected. Two ramps, because
   this colour is set inline as `--tone` and so can't be answered by the
   light-theme stylesheet: the dark ramp is pastel for a near-black card
   (mint/amber/rose), the light one its darkened twin, each clearing AA on
   its own surface. Same three steps, same meaning. */
const TONES = {
  dark: ['#6fffd1', '#ffc46a', '#ff7a90'],
  light: ['#047857', '#b45309', '#b91c1c'],
};
const toneFor = (score, theme = 'dark') => {
  const ramp = TONES[theme === 'light' ? 'light' : 'dark'];
  return score >= 80 ? ramp[0] : score >= 60 ? ramp[1] : ramp[2];
};

const verdictFor = (score) => (
  score >= 80
    ? ['Strong match', 'Your resume lines up with this posting. Fix the remaining gaps and send it.']
    : score >= 60
      ? ['Borderline', 'It will clear some filters but not all. Close the keyword gaps below first.']
      : ['High reject risk', 'Most ATS engines would filter this out. Work through the gaps before applying.']
);

/* Flattens a saved profile into resume-ish plain text for the analyzer. */
const buildResumeTextFromProfile = (profile) => {
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.phone) text += `Phone: ${profile.phone}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  if (profile.github) text += `GitHub: ${profile.github}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
    text += 'WORK EXPERIENCE\n';
    profile.workExperience.forEach(exp => {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `${exp.description}\n`;
        text += '\n';
      }
    });
  }
  if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
    text += 'EDUCATION\n';
    profile.education.forEach(edu => {
      if (edu.degree || edu.institution) {
        text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
      }
    });
    text += '\n';
  }
  if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
    text += 'PROJECTS\n';
    profile.projects.forEach(proj => {
      if (proj.name) {
        text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
        if (proj.description) text += `${proj.description}\n`;
        text += '\n';
      }
    });
  }
  if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
  return text.trim();
};

/* A profile is only usable as a resume if it has real content in it. */
const profileHasResume = (p) => !!p && !!(
  p.professionalSummary ||
  p.skills ||
  (Array.isArray(p.workExperience) && p.workExperience.some(e => e.company || e.position))
);

async function extractTextFromFile(file) {
  const name = (file.name || '').toLowerCase();
  if (file.type === 'text/plain' || name.endsWith('.txt')) {
    return (await file.text()).trim();
  }
  if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      out += content.items.map(it => it.str).join(' ') + '\n';
    }
    const text = out.trim();
    if (!text) throw new Error('That PDF has no selectable text — it may be a scan. Paste the text instead.');
    return text;
  }
  if (name.endsWith('.docx') || name.endsWith('.doc') || (file.type || '').includes('word')) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return (r.value || '').trim();
  }
  throw new Error('Unsupported file type. Upload a PDF, DOCX, DOC, or TXT.');
}

const ATSCheckStudio = ({ setShowAuthModal }) => {
  const navigate = useNavigate();
  const { user, studioTheme } = useUser();
  const gate = useFeatureGate('atsCheck');

  const [jobDescription, setJobDescription] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [jdFileName, setJdFileName] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [usingProfile, setUsingProfile] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [busyFile, setBusyFile] = useState('');       // '' | 'resume' | 'jd'
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const resumeFileRef = useRef(null);
  const jdFileRef = useRef(null);
  const reportRef = useRef(null);

  /* Pull the saved profile so the user can scan without re-uploading. */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/profile`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data?.profile) setProfileData(data.profile); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const hasProfileResume = profileHasResume(profileData);
  const canRun = !!jobDescription.trim() && !!resumeText.trim() && !analyzing && !busyFile;

  const handleFile = useCallback(async (file, kind) => {
    if (!file) return;
    setBusyFile(kind);
    setError('');
    try {
      const text = await extractTextFromFile(file);
      if (!text) throw new Error('No readable text found in that file.');
      if (kind === 'resume') {
        setResumeText(text);
        setResumeFileName(file.name);
        setUsingProfile(false);
      } else {
        setJobDescription(text);
        setJdFileName(file.name);
      }
    } catch (e) {
      setError(e.message || 'Could not read that file. Try pasting the text instead.');
    } finally {
      setBusyFile('');
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer?.files?.[0], 'resume');
  }, [handleFile]);

  const useProfileResume = useCallback(() => {
    if (!hasProfileResume) return;
    setResumeText(buildResumeTextFromProfile(profileData));
    setResumeFileName('');
    setUsingProfile(true);
    setError('');
  }, [hasProfileResume, profileData]);

  const handleAnalyze = useCallback(async () => {
    if (!user) { setShowAuthModal?.(true); return; }
    if (!jobDescription.trim()) { setError('Paste the job description first.'); return; }
    if (!resumeText.trim()) { setError('Add your resume — upload a file, use your profile, or paste it.'); return; }
    if (gate.locked) { gate.openPaywall(); return; }

    setAnalyzing(true);
    setError('');
    try {
      // Deduct before calling the model. Subscribers bypass on the server.
      const { ok, reason } = await gate.deduct();
      if (!ok) {
        if (reason !== 'no-credits') setError(reason || 'Could not start the scan. Try again.');
        return;
      }
      const report = await analyzeATS(resumeText, jobDescription);
      setResult(report);
      // Stacked layout (narrow screens) — bring the report into view.
      if (window.innerWidth <= 1080) {
        requestAnimationFrame(() => reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
    } catch (e) {
      setError(e.message || 'The scan failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  }, [user, setShowAuthModal, jobDescription, resumeText, gate]);

  const handleClear = useCallback(() => {
    setJobDescription('');
    setResumeText('');
    setResumeFileName('');
    setJdFileName('');
    setUsingProfile(false);
    setResult(null);
    setError('');
  }, []);

  const score = result?.overallScore ?? 0;
  const tone = toneFor(score, studioTheme);
  const [verdict, verdictSub] = useMemo(() => verdictFor(score), [score]);

  const keywordPct = result && result.totalKeywords
    ? Math.round((result.keywordMatches.length / result.totalKeywords) * 100)
    : 0;
  const formatPct = result ? Math.min(100, Math.round((result.formatScore || 0) * 10)) : 0;

  // Ring geometry — r=57 keeps a 6px stroke inside the 128px box.
  const R = 57;
  const CIRC = 2 * Math.PI * R;

  return (
    <section className="acs">
      {/* Theme switch — parks itself beside the header pill; see
          `.cls-theme-dock` in studioTheme.css. Out of flow in both layouts,
          so the viewport-locked workspace keeps all of its height. */}

      <div className="acs-workspace">
        {/* ---------------- LEFT: inputs ---------------- */}
        <div className="acs-left">
          <div className="acs-left-scroll">
            {/* Job description */}
            <div className="acs-panel">
              <div className="acs-panel-head">
                <span className="acs-panel-title">Job Description</span>
                {jobDescription.trim() && (
                  <span className="acs-count">{jobDescription.trim().split(/\s+/).length} words</span>
                )}
                <button
                  type="button"
                  className="acs-btn acs-btn-ghost acs-btn-sm"
                  onClick={() => jdFileRef.current?.click()}
                  disabled={!!busyFile || analyzing}
                >
                  {busyFile === 'jd' ? 'Reading…' : 'Upload file'}
                </button>
                <input
                  ref={jdFileRef}
                  type="file"
                  accept=".txt,.doc,.docx,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => { handleFile(e.target.files?.[0], 'jd'); e.target.value = ''; }}
                />
              </div>
              <textarea
                className="acs-textarea"
                rows={7}
                value={jobDescription}
                onChange={(e) => { setJobDescription(e.target.value); setError(''); }}
                placeholder="Paste the job posting here — the whole thing, requirements included. The more complete it is, the sharper the keyword analysis."
                disabled={analyzing}
              />
              {jdFileName && <div className="acs-file">📄 {jdFileName}</div>}
            </div>

            {/* Resume */}
            <div className="acs-panel">
              <div className="acs-panel-head">
                <span className="acs-panel-title">Your Resume</span>
                {resumeText.trim() && (
                  <span className="acs-count">{resumeText.trim().split(/\s+/).length} words</span>
                )}
              </div>

              <div className={`acs-source ${hasProfileResume ? 'has-profile' : ''}`}>
                <div
                  className={`acs-drop ${dragging ? 'dragging' : ''} ${busyFile === 'resume' ? 'busy' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => resumeFileRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resumeFileRef.current?.click(); } }}
                  onDrop={onDrop}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                >
                  <span className="acs-drop-icon" aria-hidden="true">📄</span>
                  <div className="acs-drop-text">
                    <div className="acs-drop-title">
                      {busyFile === 'resume' ? 'Reading your resume…' : resumeFileName || 'Upload or drop your resume'}
                    </div>
                    <div className="acs-drop-sub">PDF, DOCX, DOC or TXT</div>
                  </div>
                </div>
                <input
                  ref={resumeFileRef}
                  type="file"
                  accept=".txt,.doc,.docx,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => { handleFile(e.target.files?.[0], 'resume'); e.target.value = ''; }}
                />

                {hasProfileResume && (
                  <button
                    type="button"
                    className={`acs-source-alt ${usingProfile ? 'active' : ''}`}
                    onClick={useProfileResume}
                    disabled={!!busyFile || analyzing}
                  >
                    <span className="acs-source-alt-title">
                      {usingProfile ? '✓ Profile resume' : 'Use profile resume'}
                    </span>
                    <span className="acs-source-alt-sub">
                      {profileData?.fullName || 'From your saved profile'}
                    </span>
                  </button>
                )}
              </div>

              <textarea
                className="acs-textarea"
                rows={10}
                value={resumeText}
                onChange={(e) => { setResumeText(e.target.value); setUsingProfile(false); setError(''); }}
                placeholder="…or paste your resume text here. Everything you load above lands in this box and stays editable — tweak it and re-scan as many times as you like."
                disabled={analyzing}
              />
            </div>

            {error && (
              <div className="acs-error">
                <span aria-hidden="true">⚠</span>
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="acs-actions">
            <button
              type="button"
              className="acs-btn acs-btn-primary"
              onClick={handleAnalyze}
              disabled={!canRun}
            >
              {analyzing ? 'Scanning…' : result ? 'Re-run ATS scan' : 'Run ATS scan'}
            </button>
            <button
              type="button"
              className="acs-btn acs-btn-ghost"
              onClick={handleClear}
              disabled={analyzing || !!busyFile}
            >
              Clear all
            </button>
          </div>
        </div>

        {/* ---------------- RIGHT: report ---------------- */}
        <div className="acs-right" ref={reportRef}>
          <div className="acs-report">
            <div className="acs-report-head">
              <span className="acs-report-title">ATS Report</span>
              {result && !analyzing && (
                <span className="acs-count" style={{ marginLeft: 'auto' }}>
                  {result.totalKeywords} keywords analyzed
                </span>
              )}
            </div>

            <div className="acs-report-body">
              {analyzing ? (
                <div className="acs-state">
                  <div className="acs-scanner" aria-hidden="true">
                    <div className="acs-scanner-lines"><i /><i /><i /><i /><i /></div>
                    <div className="acs-scanner-beam" />
                  </div>
                  <div className="acs-state-title">Scanning your resume</div>
                  <p className="acs-state-sub">
                    Pulling keywords out of the posting, then matching them against your resume the
                    way a real ATS parser would.
                  </p>
                </div>
              ) : result ? (
                <>
                  <div className="acs-score" style={{ '--tone': tone }}>
                    <div className="acs-ring">
                      <svg width="128" height="128" viewBox="0 0 128 128">
                        <circle className="acs-ring-track" cx="64" cy="64" r={R} fill="none" strokeWidth="6" />
                        <circle
                          className="acs-ring-fill"
                          cx="64" cy="64" r={R} fill="none" strokeWidth="6"
                          strokeDasharray={CIRC}
                          strokeDashoffset={CIRC - (CIRC * Math.min(100, Math.max(0, score))) / 100}
                        />
                      </svg>
                      <div className="acs-ring-val">
                        <span className="acs-ring-num">{score}</span>
                        <span className="acs-ring-cap">ATS Match</span>
                      </div>
                    </div>
                    <div className="acs-score-copy">
                      <p className="acs-verdict">{verdict}</p>
                      <p className="acs-verdict-sub">{verdictSub}</p>
                    </div>
                  </div>

                  <div className="acs-stats">
                    <div className="acs-stat" style={{ '--tone': toneFor(keywordPct, studioTheme) }}>
                      <div className="acs-stat-label">Keyword match</div>
                      <div className="acs-stat-val">
                        {result.keywordMatches.length}<small> / {result.totalKeywords}</small>
                      </div>
                      <div className="acs-bar"><i style={{ width: `${keywordPct}%` }} /></div>
                    </div>
                    <div className="acs-stat" style={{ '--tone': toneFor(formatPct, studioTheme) }}>
                      <div className="acs-stat-label">ATS readability</div>
                      <div className="acs-stat-val">
                        {result.formatScore}<small> / 10</small>
                      </div>
                      <div className="acs-bar"><i style={{ width: `${formatPct}%` }} /></div>
                    </div>
                  </div>

                  <div className="acs-sec">
                    <div className="acs-sec-head">
                      <h4>Keywords</h4>
                      <span className="acs-sec-note">Pulled from the job description</span>
                    </div>

                    <div className="acs-kw-group">
                      <div className="acs-kw-group-label">
                        Missing ({result.missingKeywords.length}) — add these where they're honestly true
                      </div>
                      {result.missingKeywords.length ? (
                        <div className="acs-kws">
                          {result.missingKeywords.map((k, i) => (
                            <span key={`${k}-${i}`} className="acs-kw missing">{k}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="acs-kw-empty">Nothing missing — full keyword coverage.</div>
                      )}
                    </div>

                    <div className="acs-kw-group">
                      <div className="acs-kw-group-label">Matched ({result.keywordMatches.length})</div>
                      {result.keywordMatches.length ? (
                        <div className="acs-kws">
                          {result.keywordMatches.map((k, i) => (
                            <span key={`${k}-${i}`} className="acs-kw matched">{k}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="acs-kw-empty">No keyword overlap detected yet.</div>
                      )}
                    </div>
                  </div>

                  <div className="acs-sec">
                    <div className="acs-sec-head"><h4>Resume completeness</h4></div>
                    <div className="acs-checks">
                      {CHECKLIST_ITEMS.map(([key, label, rec]) => {
                        const present = !!result.checklist?.[key];
                        return (
                          <div key={key} className={`acs-check ${present ? '' : 'miss'}`}>
                            <span className="acs-check-mark" aria-hidden="true">{present ? '✓' : '✕'}</span>
                            <div className="acs-check-body">
                              <div className="acs-check-label">{label}</div>
                              {!present && <div className="acs-check-rec">{rec}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {!!result.formatRecommendations?.length && (
                    <div className="acs-sec">
                      <div className="acs-sec-head"><h4>Format fixes</h4></div>
                      <ul className="acs-recs">
                        {result.formatRecommendations.map((rec, i) => <li key={i}>{rec}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="acs-next">
                    <button
                      type="button"
                      className="acs-btn acs-btn-primary"
                      onClick={() => navigate('/resume-studio')}
                    >
                      Tailor this resume
                    </button>
                    <button
                      type="button"
                      className="acs-btn acs-btn-ghost"
                      onClick={() => navigate('/CoverLetter')}
                    >
                      Write a cover letter
                    </button>
                  </div>
                </>
              ) : (
                <div className="acs-state">
                  <div className="acs-state-icon" aria-hidden="true">🎯</div>
                  <div className="acs-state-title">Your report lands here</div>
                  <p className="acs-state-sub">
                    One score, every missing keyword, and the exact format problems that get resumes
                    filtered out — in about a second.
                  </p>
                  <div className="acs-state-steps">
                    <div className={`acs-state-step ${jobDescription.trim() ? 'done' : ''}`}>
                      <span className="acs-state-num">{jobDescription.trim() ? '✓' : '1'}</span>
                      Paste the job description
                    </div>
                    <div className={`acs-state-step ${resumeText.trim() ? 'done' : ''}`}>
                      <span className="acs-state-num">{resumeText.trim() ? '✓' : '2'}</span>
                      Add your resume
                    </div>
                    <div className="acs-state-step">
                      <span className="acs-state-num">3</span>
                      Hit Run ATS scan
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ATSCheckStudio;
